import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { Plus, Pencil, BadgeCheck } from 'lucide-react';
import { formatName, getInitials } from '@/lib/utils';
import PhoneField from '@/components/PhoneField';
import FieldShell from '@/components/FieldShell';
import { validateFirstName, validateLastName, validateEmail, validatePhoneE164, validatePassword, countErrors } from '@/lib/validators';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const EMPTY_FORM = { email: '', password: '', first_name: '', last_name: '', phone: '', qualification: '', specializations: '', branch_ids: [], is_admin: false, ncahp_id: '', apa_id: '' };

export default function TherapistsPage() {
  const [therapists, setTherapists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const busyRef = useRef(false);

  const isEdit = !!editingId;

  const fetch = async () => {
    try {
      const [tRes, bRes] = await Promise.all([api.get('/therapists'), api.get('/branches')]);
      setTherapists(tRes.data);
      setBranches(bRes.data);
    } catch {}
  };
  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({}); setTouched({});
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditingId(t._id);
    setForm({
      email: t.email || '', password: '', first_name: t.first_name || '', last_name: t.last_name || '',
      phone: t.phone || '', qualification: t.qualification || '',
      specializations: (t.specializations || []).join(', '),
      branch_ids: t.branch_ids || [], is_admin: t.role === 'ADMIN',
      ncahp_id: t.ncahp_id || '', apa_id: t.apa_id || '',
    });
    setErrors({}); setTouched({});
    setShowModal(true);
  };

  const fieldError = (key, value) => {
    switch (key) {
      case 'first_name': return validateFirstName(value);
      case 'last_name': return validateLastName(value);
      case 'email': return validateEmail(value);
      case 'phone': return validatePhoneE164(value);
      case 'password': return isEdit ? '' : validatePassword(value);
      default: return '';
    }
  };

  const validateAll = () => {
    const keys = ['first_name', 'last_name', 'email', 'phone'];
    if (!isEdit) keys.push('password');
    const next = {};
    keys.forEach(k => { next[k] = fieldError(k, form[k]); });
    return next;
  };

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (touched[key]) setErrors(prev => ({ ...prev, [key]: fieldError(key, value) }));
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: fieldError(key, form[key]) }));
  };

  const toggleBranch = (id) => {
    setForm(prev => ({
      ...prev,
      branch_ids: prev.branch_ids.includes(id) ? prev.branch_ids.filter(b => b !== id) : [...prev.branch_ids, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busyRef.current) return;
    const errs = validateAll();
    setErrors(errs);
    setTouched({ first_name: true, last_name: true, email: true, phone: true, password: true });
    if (countErrors(errs) > 0) {
      toast.error(`Please fix ${countErrors(errs)} error${countErrors(errs) > 1 ? 's' : ''} before saving.`);
      return;
    }
    busyRef.current = true;
    setSubmitting(true);
    const specs = form.specializations ? form.specializations.split(',').map(s => s.trim()).filter(Boolean) : [];
    try {
      if (isEdit) {
        await api.put(`/therapists/${editingId}`, {
          first_name: form.first_name, last_name: form.last_name, email: form.email,
          phone: form.phone, qualification: form.qualification, specializations: specs,
          branch_ids: form.branch_ids, is_admin: form.is_admin,
          ncahp_id: form.ncahp_id, apa_id: form.apa_id,
        });
        toast.success('Therapist profile updated successfully.');
      } else {
        await api.post('/therapists', { ...form, specializations: specs });
        toast.success('Therapist created successfully.');
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      fetch();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Something went wrong. Please try again.');
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  };

  const toggleActive = async (t) => {
    if (t.is_active !== false) await api.put(`/therapists/${t._id}/deactivate`);
    else await api.put(`/therapists/${t._id}/activate`);
    fetch();
  };

  return (
    <div className="space-y-6" data-testid="therapists-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Therapists</h1>
        <Button onClick={openCreate} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-therapist-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Therapist
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="therapist-cards">
        {therapists.map(t => (
          <Card key={t._id} className="bg-white border-[#E5F2F1] rounded-2xl hover:shadow-[0_8px_30px_rgba(13,139,125,0.08)] transition-all duration-300">
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#0A6F64]/10 flex items-center justify-center text-[#0A6F64] font-semibold text-sm flex-shrink-0">
                  {getInitials(t) || t.first_name?.[0] || '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#1A3330]">{formatName(t)}</p>
                  <p className="text-xs text-[#4A6B68]">{t.email}</p>
                  <p className="text-xs text-[#4A6B68]">{t.qualification}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => openEdit(t)}
                    className="h-7 w-7 text-[#0D8B7D] hover:bg-[#0D8B7D]/10 rounded-lg"
                    title="Edit therapist"
                    data-testid={`edit-therapist-${t._id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Badge className={`text-[10px] border-0 ${t.role === 'ADMIN' ? 'bg-[#F5B041]/10 text-[#F5B041]' : 'bg-[#0D8B7D]/10 text-[#0D8B7D]'}`}>{t.role}</Badge>
                  <Badge className={`text-[10px] border-0 ${t.is_active !== false ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-[#D3544E]/10 text-[#D3544E]'}`}>{t.is_active !== false ? 'Active' : 'Inactive'}</Badge>
                </div>
              </div>
              {t.specializations?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {t.specializations.map(s => <Badge key={s} className="bg-[#F4FAF9] text-[#4A6B68] text-[10px] border-0 font-normal">{s}</Badge>)}
                </div>
              )}

              {/* Professional Registrations */}
              <div className="border-t border-[#E5F2F1] pt-3 mb-3" data-testid={`prof-reg-${t._id}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#0A6F64]" />
                  <p className="text-[11px] font-semibold text-[#1A3330] uppercase tracking-wide">Professional Registrations</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-[#4A6B68]">NCAHP Registration ID</p>
                    <p className="text-xs text-[#1A3330]">{t.ncahp_id || <span className="text-[#9CB3B0] italic">Not provided</span>}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#4A6B68]">APA Membership ID</p>
                    <p className="text-xs text-[#1A3330]">{t.apa_id || <span className="text-[#9CB3B0] italic">Not provided</span>}</p>
                  </div>
                </div>
              </div>

              <Button size="sm" variant="ghost" onClick={() => toggleActive(t)} className={`text-xs h-7 ${t.is_active !== false ? 'text-[#D3544E]' : 'text-[#2E8B57]'}`} data-testid={`toggle-therapist-${t._id}`}>{t.is_active !== false ? 'Deactivate' : 'Activate'}</Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={(o) => { if (!submitting) { setShowModal(o); if (!o) { setEditingId(null); setForm(EMPTY_FORM); } } }}>
        <DialogContent className="max-w-lg bg-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="therapist-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">{isEdit ? 'Edit Therapist' : 'Add Therapist'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FieldShell label="First Name" required error={touched.first_name ? errors.first_name : ''} testId="therapist-first-name-shell">
                <Input value={form.first_name} onChange={e => setField('first_name', e.target.value)} onBlur={() => handleBlur('first_name')} className="rounded-xl border-[#E5F2F1]" data-testid="therapist-first-name" />
              </FieldShell>
              <FieldShell label="Last Name" error={touched.last_name ? errors.last_name : ''} testId="therapist-last-name-shell">
                <Input value={form.last_name} onChange={e => setField('last_name', e.target.value)} onBlur={() => handleBlur('last_name')} className="rounded-xl border-[#E5F2F1]" data-testid="therapist-last-name" />
              </FieldShell>
              <FieldShell label="Email" required error={touched.email ? errors.email : ''} testId="therapist-email-shell">
                <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)} onBlur={() => handleBlur('email')} className="rounded-xl border-[#E5F2F1]" data-testid="therapist-email" />
              </FieldShell>
              {!isEdit && (
                <FieldShell label="Password" required error={touched.password ? errors.password : ''} testId="therapist-password-shell">
                  <Input type="password" value={form.password} onChange={e => setField('password', e.target.value)} onBlur={() => handleBlur('password')} className="rounded-xl border-[#E5F2F1]" data-testid="therapist-password" />
                </FieldShell>
              )}
              <div>
                <Label className="text-xs text-[#1A3330] font-medium block mb-1">Phone <span className="text-[#D3544E]">*</span></Label>
                <PhoneField value={form.phone} onChange={v => setField('phone', v)} error={!!(touched.phone && errors.phone)} testId="therapist-phone" inputProps={{ onBlur: () => handleBlur('phone') }} />
                {touched.phone && errors.phone && <p className="text-[10px] text-[#D3544E] mt-1" data-testid="therapist-phone-error">{errors.phone}</p>}
              </div>
              <div><Label className="text-xs text-[#1A3330] font-medium block mb-1">Qualification</Label><Input value={form.qualification} onChange={e => setField('qualification', e.target.value)} placeholder="M.Phil Clinical Psych" className="rounded-xl border-[#E5F2F1]" data-testid="therapist-qualification" /></div>
              <div className="col-span-2"><Label className="text-xs text-[#1A3330] font-medium block mb-1">Specializations (comma separated)</Label><Input value={form.specializations} onChange={e => setField('specializations', e.target.value)} placeholder="Depression, Anxiety, PTSD..." className="rounded-xl border-[#E5F2F1]" data-testid="therapist-specializations" /></div>

              <div className="col-span-2">
                <Label className="text-xs text-[#1A3330] font-medium block mb-1.5">Branch Assignment</Label>
                <div className="flex flex-wrap gap-2" data-testid="therapist-branches">
                  {branches.map(b => {
                    const active = form.branch_ids.includes(b._id);
                    return (
                      <button type="button" key={b._id} onClick={() => toggleBranch(b._id)}
                        className={`px-3 py-1.5 rounded-xl text-xs border transition-colors ${active ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'bg-white text-[#4A6B68] border-[#E5F2F1] hover:border-[#0D8B7D]'}`}
                        data-testid={`branch-toggle-${b._id}`}>
                        {b.name}
                      </button>
                    );
                  })}
                  {branches.length === 0 && <p className="text-xs text-[#4A6B68]">No branches available.</p>}
                </div>
              </div>

              {/* Professional Registrations */}
              <div className="col-span-2 border-t border-[#E5F2F1] pt-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <BadgeCheck className="w-4 h-4 text-[#0A6F64]" />
                  <p className="text-sm font-semibold text-[#1A3330] font-['Outfit']">Professional Registrations</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-[#1A3330] font-medium block mb-1">NCAHP Registration ID</Label><Input value={form.ncahp_id} onChange={e => setField('ncahp_id', e.target.value)} placeholder="Optional" className="rounded-xl border-[#E5F2F1]" data-testid="therapist-ncahp-id" /></div>
                  <div><Label className="text-xs text-[#1A3330] font-medium block mb-1">APA Membership ID</Label><Input value={form.apa_id} onChange={e => setField('apa_id', e.target.value)} placeholder="Optional" className="rounded-xl border-[#E5F2F1]" data-testid="therapist-apa-id" /></div>
                </div>
              </div>

              <div className="col-span-2 flex items-center gap-3 border-t border-[#E5F2F1] pt-3">
                <Switch checked={form.is_admin} onCheckedChange={v => setField('is_admin', v)} data-testid="therapist-admin-toggle" />
                <Label className="text-sm">Admin Role</Label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-therapist-btn">
                {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Therapist')}
              </Button>
              <Button type="button" variant="outline" disabled={submitting} onClick={() => setShowModal(false)} className="rounded-xl border-[#E5F2F1]" data-testid="cancel-therapist-btn">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
